import { Chart } from 'chart.js'
import { toFontString, toTRBL, color } from 'chart.js/helpers'
import log from 'loglevel'

/**
 * alignの定義
 */
const ALIGN = {
  START: 'start',
  END: 'end',
  CENTER: 'center',
}

/**
 * 指定方向のpadding値を取得する
 * @param {number|Object} padding padding定義
 * @param {string} direction 方向(left, right, top, bottom)
 * @returns {number} padding値
 */
const getPaddingValue = (padding, direction) => {
  return toTRBL(padding)[direction] || 0
}

/**
 * テキストが描画されるサイズを取得する
 * @param {CanvasRenderingContext2D} ctx コンテキスト
 * @param {string} text 描画するテキスト
 * @param {number|Object} padding padding定義
 * @param {string|Object} font フォント設定
 * @returns {Object} サイズ(width, height)
 */
const getActualTextSize = (ctx, text, padding, font) => {
  if (font) {
    ctx.save()
    ctx.font = toFontString(font)
  }
  const { width, actualBoundingBoxAscent, actualBoundingBoxDescent } =
    ctx.measureText(text)
  if (font) {
    ctx.restore()
  }

  let textWidth = width
  let textHeight = actualBoundingBoxAscent + actualBoundingBoxDescent

  if (!padding) {
    return { width: textWidth, height: textHeight }
  }

  // X方向padding
  textWidth +=
    getPaddingValue(padding, 'left') + getPaddingValue(padding, 'right')
  // Y方向padding
  textHeight +=
    getPaddingValue(padding, 'top') + getPaddingValue(padding, 'bottom')

  return { width: textWidth, height: textHeight }
}

/**
 * ベースとなるX方向offset値を取得する
 * @param {number} width テキスト描画幅
 * @param {string} align 文字揃え方向
 * @param {number|Object} padding padding定義
 * @returns {number} offset値
 */
const getBaseOffsetX = (width, align, padding) => {
  const alignSidePadding = getAlignSidePaddingValue(align, padding)
  switch (align) {
    case ALIGN.START:
      return alignSidePadding
    case ALIGN.CENTER:
      return -width / 2 + alignSidePadding
    case ALIGN.END:
      return -width
    default:
      return 0
  }
}

/**
 * align方向のpadding値を取得する
 * @param {string} align 文字揃え方向
 * @param {number|Object} padding padding定義
 * @returns {number} alignに指定された方向のpadding値
 */
const getAlignSidePaddingValue = (align, padding) => {
  switch (align) {
    case ALIGN.START:
      return getPaddingValue(padding, 'left')
    case ALIGN.CENTER:
      return 0
    case ALIGN.END:
      return -getPaddingValue(padding, 'right')
    default:
      return 0
  }
}

/**
 * Y-Axis の title を Axis の端点基準で発生させるプラグイン
 *
 * option
 * * display {boolean} 表示フラグ
 * * text {string} 表示テキスト
 * * align {string} 揃え位置('start', 'center', 'end')
 * * font {Font} フォント指定
 * * color {Color} 色指定
 * * padding {Padding} padding幅(px)
 * * offsetX {number} x方向オフセット(px)
 * * offsetY {number} y報告オフセット(px)
 */
export default {
  id: 'yTitleOnScaleEndPlugin',
  beforeLayout: (chart, args, options) => {
    if (!options.display) return

    const {
      ctx,
      scales: {
        y: { width: scaleWidth },
      },
    } = chart
    const { text, padding, font, align, offsetX, offsetY } = options
    const { width, height } = getActualTextSize(ctx, text, padding, font)

    // 上側 padding 設定
    chart.options.layout.padding.top = -offsetY + height

    // 左側 padding 設定
    const baseOffsetX = getBaseOffsetX(width, align, padding) + offsetX
    if (-baseOffsetX > scaleWidth) {
      chart.options.layout.padding.left = -baseOffsetX - scaleWidth
    }
    log.debug('padding [left, top]', [
      chart.options.layout.padding.left,
      chart.options.layout.padding.top,
    ])
  },
  afterDraw: (chart, args, options) => {
    const {
      ctx,
      chartArea: { top, left },
    } = chart

    const {
      display,
      text,
      color: colorOpts,
      font,
      align,
      padding,
      offsetX,
      offsetY,
    } = options

    if (display) {
      ctx.save()
      ctx.fillStyle = color(colorOpts).hexString()
      ctx.font = toFontString(font)
      ctx.textAlign = align

      const { height } = getActualTextSize(ctx, text, padding)

      ctx.fillText(
        text,
        left + getAlignSidePaddingValue(align, padding) + offsetX,
        top - height / 2 + offsetY
      )
      ctx.restore()
    }
  },
  defaults: {
    display: true,
    text: '',
    align: ALIGN.CENTER,
    font: Chart.defaults.font,
    color: Chart.defaults.color,
    offsetX: 0,
    offsetY: -8,
    padding: 8,
  },
}
